import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormCheckbox } from '@/components/form/FormCheckbox'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentEducation } from '@/features/student/api/profile.api'
import { useEducationMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  institution_name: z.string().min(1, 'Institution is required'),
  degree: z.string().min(1, 'Degree is required'),
  course: z.string().optional(),
  specialization: z.string().optional(),
  board_or_university: z.string().optional(),
  start_year: z.string().min(1, 'Start year is required'),
  end_year: z.string().optional(),
  percentage_or_cgpa: z.string().optional(),
  is_current: z.boolean(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentEducation): FormValues {
  return {
    institution_name: item?.institutionName ?? '',
    degree: item?.degree ?? '',
    course: item?.course ?? '',
    specialization: item?.specialization ?? '',
    board_or_university: item?.boardOrUniversity ?? '',
    start_year: String(item?.startYear ?? new Date().getFullYear()),
    end_year: item?.endYear ? String(item.endYear) : '',
    percentage_or_cgpa: item?.percentageOrCgpa ?? '',
    is_current: item?.isCurrent ?? false,
  }
}

export function EducationSection({ items }: { items: StudentEducation[] }) {
  const [editing, setEditing] = useState<StudentEducation | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useEducationMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const payload = {
      ...values,
      start_year: Number(values.start_year),
      end_year: values.end_year ? Number(values.end_year) : undefined,
    }
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(payload, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No education added yet.</p>}
      {items.map((item) => (
        <div
          key={item.id}
          className="d-flex justify-content-between align-items-start border-bottom py-2"
        >
          <div>
            <p className="fw-medium mb-0">
              {item.degree} · {item.institutionName}
            </p>
            <p className="fs-12 text-muted mb-0">
              {item.startYear} — {item.isCurrent ? 'Present' : (item.endYear ?? '—')}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(item)}>
              Edit
            </Button>
            <Button
              variant="danger"
              confirmTitle="Remove this education entry?"
              onClick={() => remove.mutate(item.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => setEditing('new')}>
        Add Education
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Education' : 'Edit Education'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="institution_name" label="Institution" required />
          <FormInput control={control} name="degree" label="Degree" required />
          <FormInput control={control} name="course" label="Course" />
          <FormInput control={control} name="specialization" label="Specialization" />
          <FormInput control={control} name="board_or_university" label="Board / University" />
          <FormInput
            control={control}
            name="start_year"
            label="Start Year"
            type="number"
            required
          />
          <FormCheckbox control={control} name="is_current" label="Currently studying here" />
          <FormInput control={control} name="end_year" label="End Year" type="number" />
          <FormInput control={control} name="percentage_or_cgpa" label="Percentage / CGPA" />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
